'use client'

import * as React from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  Table as TanstackTable,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'

import { DataTablePagination } from './../data-table/data-table-pagination'
import { DataTableToolbar } from './data-table-toolbar-manual'
import { useEffect } from 'react'
interface DataTableProps<TData, TValue> {
  data: TData[]
  columns: ColumnDef<TData, TValue>[]
  toolbar?: (table: TanstackTable<TData>) => React.ReactNode
  renderRowAction?: (row: TData) => React.ReactNode
  rowCount: number
  pageIndex: number
  pageSize: number
  onPaginationChange: any
  onSortingChange: any
  onColumnFiltersChange: any
  onRowClick?: (row: TData) => void
  showSelectedRowCount?: boolean
  tableTextSize?: string
  defaultSorting?: SortingState
  className?: string
  isMultiSelect?: boolean
  onMultiSelectChange?: (value: boolean) => void
  onRowSelectionChange?: (selection: Record<string, boolean>) => void
  isLoading?: boolean
  emptyStateLabel?: string
  onExportCsv?: () => Promise<TData[]> | TData[]
  exportFileName?: string
  isExporting?: boolean
}

export function DataTableManual<TData, TValue>({
  columns,
  data,
  toolbar,
  rowCount,
  pageIndex,
  pageSize,
  onPaginationChange,
  onSortingChange,
  onColumnFiltersChange,
  showSelectedRowCount = false,
  onRowClick,
  className,
  defaultSorting = [],
  isLoading = false,
  emptyStateLabel = 'No results.',
  onExportCsv,
  exportFileName,
  isExporting = false,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({})
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )
  const [sorting, setSorting] = React.useState<SortingState>(defaultSorting)
  const [isMultiSelect, setIsMultiSelect] = React.useState(false)
  const [csvExportLoading, setCsvExportLoading] = React.useState(false)

  useEffect(() => {
    if (onSortingChange) {
      onSortingChange(sorting)
    }
  }, [sorting])

  useEffect(() => {
    if (onColumnFiltersChange) {
      onColumnFiltersChange(columnFilters)
    }
  }, [columnFilters])

  const handleCsvExport = React.useCallback(async () => {
    if (!onExportCsv) return

    try {
      setCsvExportLoading(true)

      // Get all data for export
      const allData = await onExportCsv()

      // Import CSV utilities dynamically to avoid bundle size issues
      const { convertToCsv, downloadCsv, generateDefaultFileName } =
        await import('@/lib/csvUtils')

      // Generate CSV content
      const csvContent = convertToCsv(allData, columns)

      // Generate filename
      const fileName = exportFileName || generateDefaultFileName('table-export')

      // Download the CSV
      downloadCsv(csvContent, fileName)
    } catch (error) {
      console.error('Error exporting CSV:', error)
      // Could add toast notification here if available
    } finally {
      setCsvExportLoading(false)
    }
  }, [onExportCsv, columns, exportFileName])

  const finalColumns = React.useMemo(() => {
    if (!isMultiSelect) return columns

    return [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={value => row.toggleSelected(!!value)}
            aria-label="Select row"
            onClick={e => e.stopPropagation()}
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      ...columns,
    ]
  }, [isMultiSelect, columns])

  const table = useReactTable({
    data,
    columns: finalColumns,
    rowCount,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    manualPagination: true,

    onPaginationChange: onPaginationChange,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    meta: {
      isMultiSelect,
      setIsMultiSelect,
    },
  })

  const pageCount = table.getPageCount() // Get the total number of pages

  return (
    <div className="space-y-4">
      {toolbar && toolbar(table)}
      <div className="rounded-md border">
        <Table className={className}>
          {' '}
          {/* Apply the tableTextSize prop here */}
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns?.length}
                  className="h-24 text-center"
                >
                  <div className="flex justify-center items-center space-x-2">
                    <svg
                      className="size-5 animate-spin text-primary"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => (onRowClick ? onRowClick(row.original) : null)}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns?.length}
                  className="h-24 text-center"
                >
                  {emptyStateLabel}
                </TableCell>
              </TableRow>
            )}
          </TableBody>{' '}
        </Table>
      </div>
      {pageCount > 1 && ( // Conditionally render DataTablePagination
        <DataTablePagination
          table={table}
          showSelectedRowCount={showSelectedRowCount}
          onExportCsv={onExportCsv ? handleCsvExport : undefined}
          isExporting={isExporting || csvExportLoading}
        />
      )}
    </div>
  )
}
